import { useState } from 'react'
import { IconCheck, IconMinus, IconInfoCircle } from '@tabler/icons-react'
import { useMediaQuery } from 'react-responsive'
import { isArray } from 'lodash'

import { Accordion, AccordionItem } from 'components/ui/accordion'
import Text from 'components/ui/text'
import { Button } from 'components/ui/buttons'
import { Badge } from 'components/ui/badge'
import { Tooltip } from 'components/ui/tooltip'
import { cn } from 'utils/classMerge'
import { CurveArrowSVG } from 'features/pricing/icons'
import { NOT_TRIAL_PLANS } from 'utils/consts.mjs'
import { isFreeTrialUsed } from 'utils/freeVersionBlock'
import { translate } from 'utils/translation'
import { CardItem, TABLE_CONTENT, TableValue } from 'features/pricing/consts'
import { MIXPANEL_PROPS } from 'utils/mixpanel'
import { useUser } from 'hooks/use-user'
import { getButtonText } from 'features/pricing/utils'
import { trackEvent } from 'utils/tracking/events-tracker'
import { EVENTS } from 'utils/tracking/events-tracker/consts'

interface TableItemValue {
  name: string
  tooltip?: string
  values: TableValue[]
  subtitle?: string | [string, number | string]
}

interface ValueDisplayProps {
  value: TableValue
  textClassName?: string
}

interface HeaderProps {
  option: CardItem
  isMobile: boolean
  isTablet: boolean
  isTrial: boolean
  onContinue: (data: { tier: string; interval: string }) => void
  interval: string
}

interface ItemRowProps {
  item: TableItemValue
  isMobile: boolean
}

interface PriceTableProps {
  className?: string
  items: readonly CardItem[]
  interval: string
  onContinue: (data: { tier: string; interval: string }) => void
}

const ValueDisplay: React.FC<ValueDisplayProps> = ({
  value,
  textClassName = 'tw-text-sm lg:tw-text-base tw-font-medium tw-text-zinc-800 tw-m-0',
}: {
  value: TableValue
  textClassName?: string
}) => {
  if (typeof value !== 'boolean') {
    return (
      <Text className={textClassName}>
        {isArray(value) ? translate(...value) : value}
      </Text>
    )
  }
  if (value) {
    return (
      <IconCheck
        aria-hidden="true"
        className="tw-mx-auto tw-h-5 tw-w-5 tw-text-zinc-950 lg:tw-h-6 lg:tw-w-6"
      />
    )
  }
  return (
    <IconMinus aria-hidden="true" className="tw-mx-auto tw-text-zinc-950" />
  )
}

const getActionText = (
  isTrial: boolean,
  isTablet: boolean,
  buttonText: string,
): string => {
  if (isTablet) return translate('onboarding_questionnaire_select')
  if (isTrial) return translate('start_free_trial_button', 7)
  return translate(buttonText)
}

const Header = ({
  option,
  isMobile,
  isTablet,
  isTrial,
  onContinue,
  interval,
}: HeaderProps) => {
  const { subscription } = useUser()
  const disabled =
    subscription?.tier === option.tier &&
    subscription?.planInterval === interval

  return (
    <th
      scope="col"
      className="tw-relative tw-z-10 tw-px-3 tw-py-3 tw-align-bottom md:tw-px-4 md:tw-py-6 lg:tw-p-6"
    >
      <div className="tw-flex-grow">
        <Text className="tw-m-0 tw-text-left tw-text-sm tw-font-bold md:tw-text-2xl">
          {option.title}
        </Text>
        {!isMobile &&
          option.description.map((text, idx) => (
            <Text
              key={idx}
              className="tw-m-0 tw-text-left tw-text-xs tw-font-normal tw-text-zinc-600"
            >
              {text}
            </Text>
          ))}
      </div>
      <div className="tw-mt-0 tw-flex tw-flex-col tw-items-start tw-gap-0 lg:tw-mt-4 lg:tw-flex-row lg:tw-gap-2">
        <Text
          className="tw-m-0 tw-mt-0.5 tw-text-2xl tw-font-bold lg:tw-text-4xl"
          as="span"
          renderChildren
        >
          {option.price.value || ''}
        </Text>
        <div className="tw-flex tw-flex-row tw-items-center lg:tw-flex-col lg:tw-items-start">
          <Text className="tw-m-0 tw-text-sm tw-font-black" as="span">
            {option.price.currency || ''}
          </Text>
          <Text
            className="tw-m-0 tw-text-xs tw-font-normal tw-lowercase tw-text-zinc-600"
            as="span"
            renderChildren
          >
            /{translate(interval)}
          </Text>
        </div>
      </div>
      <Button
        className={cn('tw-mt-4 tw-h-10 tw-w-full tw-bg-orange-500 !tw-p-0', {
          'tw-bg-zinc-300': disabled,
        })}
        data-testid={`card-button-${option.tier}-${interval}`}
        onClick={() => {
          trackEvent({
            event: EVENTS.PLAN_SELECT_CLICK,
            mixpanelProperties: [
              {
                tier: option.tier,
                planInterval: interval,
                checkoutModalButtonLocation:
                  MIXPANEL_PROPS.CHECKOUT_MODAL_BUTTON_LOCATION.values
                    .PLANS_COMPARISON_TABLE,
                source: MIXPANEL_PROPS.SOURCE.values.NEW_CHECKOUT_MODAL,
              },
            ],
          })
          onContinue({
            tier: option.tier,
            interval,
          })
        }}
        disabled={disabled}
        size="lg"
      >
        <Text className="tw-m-0 tw-normal-case tw-text-white">
          {translate(
            getButtonText({
              subscription,
              tier: option.tier,
              interval,
              defaultText: getActionText(isTrial, isTablet, option.buttonText),
              isSmallResolution: isTablet,
            }),
          )}
        </Text>
      </Button>
    </th>
  )
}

const ItemRow = ({ item, isMobile }: ItemRowProps) => (
  <tr>
    <th scope="row" className="tw-min-w-[180px] tw-pl-0 md:tw-pl-4">
      <div className="tw-flex tw-flex-col tw-py-1.5">
        <div className="tw-flex tw-items-start tw-gap-2">
          {!isMobile && item.tooltip && (
            <Tooltip
              content={
                <Text className="tw-m-0 tw-text-white">{item.tooltip}</Text>
              }
            >
              <IconInfoCircle className="tw-hidden tw-h-6 tw-min-h-6 tw-w-6 tw-min-w-6 tw-text-zinc-400 md:tw-flex" />
            </Tooltip>
          )}
          <Text className="tw-m-0 tw-text-left tw-text-sm tw-font-normal tw-leading-6 tw-text-zinc-800 lg:tw-text-base">
            {item.name}
          </Text>
        </div>
        {item.subtitle && (
          <Text className="tw-m-0 tw-text-xs tw-font-normal">
            {isArray(item.subtitle)
              ? translate(...item.subtitle)
              : translate(item.subtitle)}
          </Text>
        )}
      </div>
    </th>
    {item.values.map((value, idx) => (
      <td
        key={`value-${idx}-${item.name}`}
        className="tw-min-w-[125px] tw-px-2 tw-py-2 tw-text-left md:tw-px-6"
      >
        <ValueDisplay value={value} />
      </td>
    ))}
  </tr>
)

const PriceTable = (props: PriceTableProps) => {
  const { className = '', items: priceItems = [], interval, onContinue } = props
  const [indexes, setIndexes] = useState<Record<number, boolean>>({})
  const isMobile = useMediaQuery({ maxWidth: 767 })
  const isTablet = useMediaQuery({ maxWidth: 1023 })

  const isTrial = !isFreeTrialUsed() && !NOT_TRIAL_PLANS.includes(interval)

  const onAccordionClick = (index: number) => {
    setIndexes((prev) => ({
      ...prev,
      [index]: !prev[index],
    }))
  }

  if (!priceItems.length) return null

  return (
    <table
      className={cn(
        'tw-relative tw-w-full tw-border-collapse tw-border-none tw-p-0',
        className,
      )}
    >
      <colgroup className="tw-p-0">
        <col className="tw-w-1/4 tw-min-w-[180px] tw-border-0 tw-border-r tw-border-solid tw-border-zinc-200 tw-p-0 md:tw-border-white" />
        <col className="tw-w-1/4 tw-min-w-[125px] tw-bg-zinc-50 tw-p-0 md:tw-bg-white" />
        <col className="tw-w-1/4 tw-min-w-[125px] tw-bg-zinc-50 tw-p-0 md:tw-bg-white" />
        <col className="tw-w-1/4 tw-min-w-[125px] tw-bg-zinc-50 tw-p-0 md:tw-bg-white" />
      </colgroup>
      <thead className="tw-sticky tw--top-4 tw-z-10 tw-bg-white tw-p-0">
        <div className="tw-absolute tw-bottom-0 tw-left-0 tw-h-px tw-w-full tw-bg-black/10" />
        <tr className="tw-p-0">
          <th
            scope="col"
            className="tw-relative tw-z-10 tw-px-4 tw-py-0 md:tw-px-0 md:tw-py-8 lg:tw-p-8 lg:tw-pl-0 2xl:tw-p-8"
            align="center"
          >
            <CurveArrowSVG className="tw-absolute tw-right-0 tw-top-0 tw-z-[-1] tw-w-[100px] md:tw-w-[160px]" />
            {isMobile ? (
              <Text className="tw-z-2 tw-relative tw-m-0 tw-text-left tw-text-3xl tw-font-bold">
                {translate('drag_and_compare').replace(
                  '<b>',
                  '<b class="tw-text-3xl tw-font-bold tw-font-[geomanistmedium] tw-text-orange-500">',
                )}
              </Text>
            ) : (
              <Text className="tw-m-0 tw-text-left tw-text-3xl tw-font-bold">
                {translate('compare_and_choose').replace(
                  '<b>',
                  '<b class="tw-text-3xl tw-font-bold tw-font-[geomanistmedium] tw-text-orange-500">',
                )}
              </Text>
            )}
          </th>
          {priceItems.map((priceItem, index) => (
            <Header
              key={`config-${priceItem.title}-${index}`}
              option={priceItem}
              isMobile={isMobile}
              isTablet={isTablet}
              isTrial={isTrial}
              onContinue={onContinue}
              interval={interval}
            />
          ))}
        </tr>
      </thead>
      <tbody className="tw-p-0">
        {TABLE_CONTENT(translate).map(
          ({ name, items, values: sectionValues, isNew }, index: number) => {
            if (items) {
              return (
                <Accordion
                  key={`section-${JSON.stringify(name)}-${index}`}
                  multipleOpen
                >
                  <AccordionItem
                    index={index}
                    onClick={() => onAccordionClick(index)}
                    icoClass="tw-hidden lg:tw-flex"
                    buttonWrapper={(children) => (
                      <tr className="tw-border-0 tw-border-t tw-border-solid tw-border-black/10 tw-p-0">
                        <th
                          scope="colgroup"
                          className="tw-order-2 tw-min-w-[180px] tw-text-left"
                        >
                          {children}
                        </th>
                        {items[0].values.map((value, idx) => (
                          <td
                            key={`value-${idx}`}
                            className={cn(
                              'tw-min-w-[125px] tw-px-2 tw-py-2 tw-text-left tw-text-sm tw-opacity-100 tw-transition-all tw-duration-300 md:tw-px-6 md:tw-py-3 lg:tw-text-base',
                              { '!tw-opacity-0': indexes[index] },
                            )}
                          >
                            <ValueDisplay value={value} />
                          </td>
                        ))}
                      </tr>
                    )}
                    contentWrapper={(children) => (
                      <tr className="tw-p-0">
                        <th scope="colgroup" colSpan={4} className="tw-p-0">
                          {children}
                        </th>
                      </tr>
                    )}
                    title={
                      <Text
                        className="tw-text-zinc-80 tw-order-2 tw-m-0 tw-flex tw-flex-nowrap tw-items-center tw-gap-1 tw-text-left tw-text-sm tw-font-bold md:tw-text-base lg:tw-text-base"
                        renderChildren
                      >
                        {isArray(name) ? translate(...name) : translate(name)}{' '}
                        {isNew && (
                          <span className="tw-hidden lg:tw-flex">
                            <Badge value="NEW" variant="orange500" />
                          </span>
                        )}
                      </Text>
                    }
                    className="tw-relative tw-border tw-border-solid tw-border-orange-500"
                  >
                    <table className="tw-w-full tw-border-collapse tw-border-none tw-p-0">
                      <colgroup className="tw-p-0">
                        <col className="tw-w-1/4 tw-min-w-[180px] tw-border-0 tw-border-r tw-border-solid tw-border-zinc-200 tw-p-0 md:tw-border-white" />
                        <col className="tw-w-1/4 tw-min-w-[125px] tw-bg-zinc-50 tw-p-0 md:tw-bg-white" />
                        <col className="tw-w-1/4 tw-min-w-[125px] tw-bg-zinc-50 tw-p-0 md:tw-bg-white" />
                        <col className="tw-w-1/4 tw-min-w-[125px] tw-bg-zinc-50 tw-p-0 md:tw-bg-white" />
                      </colgroup>
                      <tbody className="tw-p-0">
                        {items.map((item, i) => (
                          <ItemRow
                            key={`item-${item.name}-${i}`}
                            isMobile={isMobile}
                            item={item}
                          />
                        ))}
                      </tbody>
                    </table>
                  </AccordionItem>
                </Accordion>
              )
            }
            return (
              <tr
                key={`row-${JSON.stringify(name)}-${index}`}
                className="tw-border-0 tw-border-t tw-border-solid tw-border-black/10 tw-p-0"
              >
                <th scope="row">
                  <Text
                    className="tw-m-0 tw-py-2 tw-pl-0 tw-text-left tw-text-sm tw-font-bold tw-text-zinc-800 md:tw-py-4 md:tw-pl-8 lg:tw-text-base"
                    renderChildren
                  >
                    {isArray(name) ? translate(...name) : translate(name)}{' '}
                    {isNew && <Badge value="NEW" variant="orange500" />}
                  </Text>
                </th>
                {sectionValues?.map((value, idx) => (
                  <td
                    key={`section-value-${idx}-${name}`}
                    className="tw-px-2 tw-py-2 tw-text-left md:tw-px-6 md:tw-py-4 md:tw-pl-6"
                  >
                    <ValueDisplay value={value} />
                  </td>
                ))}
              </tr>
            )
          },
        )}
      </tbody>
    </table>
  )
}

export { PriceTable }
